import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import moment from "moment";
import * as React from "react";
import {useMemo, useState} from "react";

import {DetailCell} from "@khanacademy/wonder-blocks-cell";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingXSmall,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {Pagination} from "@khan/component-simple-table";

import type {guideTranscripts_aiGuideThreads_threads as ThreadType} from "./guide-transcripts/__graphql-types__/guideTranscripts.ts";
import {ChatIcon} from "./icons/chat-icon.tsx";
import {ClockIcon} from "./icons/clock-icon.tsx";
import {DocumentIcon} from "./icons/document-icon.tsx";
import {
    countNumberOfInteractions,
    formatDateHeaderString,
    formatPageInfoString,
} from "./utils.ts";

import infoIcon from "@phosphor-icons/core/bold/info-bold.svg";

interface Props {
    threads: Array<ThreadType>;
    profilePath: string;
    /**
     * The number of items to show per page. If not provided, pagination will not be rendered.
     */
    pageSize?: number;
    /**
     * Current page number for pagination
     */
    currentPage?: number;
    /**
     * Page change handler for pagination
     */
    onPageChange?: (nextPage: number) => void;
    /**
     * Whether there are more pages available
     */
    hasMorePages?: boolean;
}

interface ThreadProps {
    thread: ThreadType;
    profilePath: string;
}

/**
 * A cell for an individual thread. The cell links to the particular
 * transcript for that thread and displays relevant info to the user, such
 * as time, conversation length, and content type. When a thread is flagged,
 * we tack on an informative label below the cell.
 */
const Thread = ({thread, profilePath}: ThreadProps): React.ReactElement => {
    const {id, lastUpdatedAt, flagged, title, coeditingDocument} = thread;
    return (
        <>
            <DetailCell
                title={
                    title ||
                    t`Khanmigo chat from ${moment(lastUpdatedAt).format("l LT")}`
                }
                testId="conversation"
                subtitle1={
                    <View style={styles.subtitle1}>
                        <ClockIcon />
                        <LabelSmall style={styles.iconLabel}>
                            {moment(lastUpdatedAt).format("LT")}
                        </LabelSmall>
                        <View style={{width: sizing.size_160}} />
                        <ChatIcon />
                        <LabelSmall style={styles.iconLabel}>
                            {countNumberOfInteractions(thread)}
                        </LabelSmall>
                        {coeditingDocument && (
                            <>
                                <View style={{width: sizing.size_160}} />
                                <DocumentIcon />
                                <LabelSmall style={styles.iconLabel}>
                                    {t`Document`}
                                </LabelSmall>
                            </>
                        )}
                    </View>
                }
                subtitle2={formatPageInfoString(thread)}
                href={`${profilePath}/khanmigo/history/${id}`}
                styles={{
                    root: [styles.threadCell, flagged && styles.moderated],
                }}
                horizontalRule="full-width"
            />
            {flagged && (
                <LabelSmall style={styles.moderatedLabel}>
                    <PhosphorIcon
                        icon={infoIcon}
                        size="small"
                        style={{
                            color: semanticColor.core.foreground.critical
                                .default,
                            alignSelf: "center",
                            marginInlineEnd: sizing.size_040,
                        }}
                    />
                    {t`This conversation with Khanmigo has been flagged by Khan Academy's moderation system.`}
                </LabelSmall>
            )}
        </>
    );
};

/**
 * This component groups the individual threads by date and displays them
 * in a sectioned list.
 */
const Threads = ({
    threads,
    profilePath,
}: Omit<
    Props,
    "pageSize" | "currentPage" | "onPageChange" | "hasMorePages"
>): React.ReactElement => {
    // Group the entire list of threads by date
    const dateGroups: Record<string, Array<ThreadType>> = threads.reduce(
        (dateGroups, thread) => {
            const date = moment(thread.lastUpdatedAt).format("l");
            if (!dateGroups[date]) {
                dateGroups[date] = [];
            }
            dateGroups[date].push(thread);
            return dateGroups;
        },
        {},
    );

    return (
        <>
            {Object.entries(dateGroups).map(([date, threadList], index) => (
                <View key={date}>
                    {index > 0 && <View style={{width: sizing.size_240}} />}
                    <HeadingXSmall style={styles.dateHeader}>
                        {formatDateHeaderString(date)}
                    </HeadingXSmall>
                    {threadList.map((thread) => (
                        <Thread
                            key={thread.id}
                            thread={thread}
                            profilePath={profilePath}
                        />
                    ))}
                </View>
            ))}
        </>
    );
};

const NoThreads = () => (
    <View>
        <Body>{t`No matching conversations were found.`}</Body>
    </View>
);

export default function ConversationsView({
    threads,
    profilePath,
    pageSize,
    currentPage = 1,
    onPageChange,
    hasMorePages = false,
}: Props): React.ReactElement {
    // Calculate paginated data
    const paginatedThreads = useMemo(() => {
        if (!pageSize) {
            return threads;
        }
        const startIdx = (currentPage - 1) * pageSize;
        const endIdx = currentPage * pageSize;
        return threads.slice(startIdx, endIdx);
    }, [threads, currentPage, pageSize]);

    // Pagination state for loading
    const [loading, setLoading] = useState(false);

    const handlePageChange = async (newPage: number) => {
        if (onPageChange) {
            setLoading(true);
            try {
                await onPageChange(newPage);
            } finally {
                setLoading(false);
            }
        }
    };

    const totalPages =
        pageSize && pageSize > 0 ? Math.ceil(threads.length / pageSize) : 1;

    return (
        <View>
            {paginatedThreads.length > 0 ? (
                <Threads threads={paginatedThreads} profilePath={profilePath} />
            ) : (
                <NoThreads />
            )}
            {totalPages > 1 && (
                <View style={styles.paginationContainer}>
                    <Pagination
                        id="conversations-pagination"
                        page={currentPage}
                        pageSize={pageSize || 1}
                        onPageChange={handlePageChange}
                        total={threads.length}
                        loading={loading}
                        hasMorePages={hasMorePages}
                    />
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    threadCell: {
        background: semanticColor.core.background.base.subtle,
    },
    moderated: {
        background: semanticColor.feedback.critical.subtle.background,
    },
    moderatedLabel: {
        color: semanticColor.core.foreground.critical.default,
        paddingInlineStart: sizing.size_080,
        paddingBlockEnd: sizing.size_160,
    },
    subtitle1: {
        flexDirection: "row",
        color: semanticColor.core.foreground.neutral.subtle,
    },
    dateHeader: {
        paddingInlineStart: sizing.size_160,
        paddingBlockEnd: sizing.size_240,
    },
    iconLabel: {
        paddingInlineStart: sizing.size_040,
    },
    paginationContainer: {
        marginBlockStart: sizing.size_240,
        marginBlockEnd: sizing.size_240,
        width: "fit-content",
        display: "inline-block",
    },
});
